/**
 * DesignMap — renders a generated design's World on the real map, read-only,
 * with its OWN MapState so it never disturbs the board's editor.world. Mirrors
 * DesignPreview's SavedDesignCanvas.
 */
import { useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { ScaleControl } from "react-map-gl";
import { CreateBrief } from "src/utils/CreateBrief";
import { useOnMount } from "src/hooks/useOnMount";
import { ToolSelection } from "src/types/MapState";
import type { MapState } from "src/types/MapState";
import type { Selection } from "src/types/Selection";
import type { World } from "src/types/World";
import { Map as TessaMap } from "../Map/Map";
import { StencilMapLayers } from "../StencilMapLayers";
import { StencilOverlay } from "../StencilOverlay";
import { Compass3D } from "../CompassWrapper";
import { MapUtilAreaOfInterestCenter } from "../MapUtilAreaOfInterestCenter";
import { MapUtilResizeObserver } from "../MapUtilResizeObserver";
import { ElevationLayer } from "../Map/Layers/Elevation.layer";
import { ExtrusionLayer } from "../Map/Layers/Extrusion";
import { ThreeLayer } from "../Map/Layers/three-layer/Three.layer";
import { ZoneEditor } from "../Map/Layers/three-layer/ZoneEditor";
import { WorldEditor } from "../WorldEditor";

export const DesignMap = observer(function DesignMap({
  world,
  mapState,
  selection,
  hideDesign,
}: {
  world: World;
  mapState: MapState;
  selection: Selection;
  /** Hide the generated buildings, leaving basemap + overlay + zone outlines (the site plan). */
  hideDesign?: boolean;
}) {
  const [defaultBrief] = useState(() => CreateBrief());
  useOnMount(() => {
    mapState.selectedTool.update(ToolSelection.SELECT);
  });
  const geoOrigin = useMemo(
    () => ({ lng: world.longitude, lat: world.latitude }),
    [world.longitude, world.latitude]
  );
  return (
    <div className="w-full h-full relative">
      <TessaMap mapState={mapState}>
        <StencilMapLayers mapState={mapState} />
        <MapUtilAreaOfInterestCenter />
        <MapUtilResizeObserver />
        <ElevationLayer mapState={mapState} />
        {!hideDesign && <ExtrusionLayer mapState={mapState} />}
        <ThreeLayer origin={geoOrigin}>
          <ZoneEditor
            enableWarnings={false}
            mapState={mapState}
            world={world}
            defaultBrief={defaultBrief}
            onSelectedToolCancel={() => mapState.selectedTool.update(ToolSelection.SELECT)}
            selectedTool={mapState.selectedTool.value.id}
            geoOrigin={geoOrigin}
            selection={selection}
          />
          {!hideDesign && (
            <WorldEditor
              mapState={mapState}
              design={world}
              onDesignChange={() => {}}
              readonly
              selection={selection}
            />
          )}
        </ThreeLayer>
        <ScaleControl />
      </TessaMap>
      <Compass3D mapState={mapState} />
      <StencilOverlay mapState={mapState} />
    </div>
  );
});
